import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import fetchLiveRankInfo, { type LiveRankData } from '@api-service/live-rank';
import { isSeasonOpen } from '@lib/utils';

const TEAM_NOT_FOUND = 404;

/**
 * Live rank failure kinds surfaced to the page so it can degrade inline
 * rather than throwing to the page-level ErrorBoundary:
 *   - 'NOT_FOUND'   : the FPL team id wasn't recognised (re-import needed)
 *   - 'UNAVAILABLE' : any 5xx / transient backend or upstream failure
 *
 * A 502 in particular is a gateway error, not specifically an FPL outage, so
 * it is treated as a transient "rank unavailable" rather than a fatal error.
 */
export type LiveRankErrorKind = 'NOT_FOUND' | 'UNAVAILABLE' | null;

const isLiveRankErrorResponse = (
  response: unknown
): response is { status: number; message: string } =>
  typeof response === 'object' && response !== null && 'status' in response;

export const useLiveRank = ({
  fplId,
  refetchInterval,
  queryKey,
  currentGameweek,
  accessToken,
  isInMaintenanceMode = false
}) => {
  const [lastFetchedTime, setLastFetchedTime] = useState('');

  const isEnabled =
    Boolean(fplId) &&
    fplId !== '0' &&
    Boolean(accessToken) &&
    isSeasonOpen(currentGameweek) &&
    !isInMaintenanceMode;

  const {
    isInitialLoading: liveRankDataIsLoading,
    data: liveRankResponse,
    isError
  } = useQuery({
    queryKey,
    // The service resolves (rather than throws) on failure, so a transient
    // outage is cached as data and the refetchInterval keeps polling until
    // the rank recovers. We never throw here, so the page stays mounted.
    queryFn: () =>
      fetchLiveRankInfo({
        teamId: fplId,
        gameweek: currentGameweek,
        accessToken
      }),
    enabled: isEnabled,
    refetchOnWindowFocus: false,
    refetchInterval
  });

  const hasErrorResponse = isLiveRankErrorResponse(liveRankResponse);

  const liveRankData: LiveRankData | null =
    !hasErrorResponse && liveRankResponse
      ? (liveRankResponse as LiveRankData)
      : null;

  const liveRankError: LiveRankErrorKind = hasErrorResponse
    ? liveRankResponse.status === TEAM_NOT_FOUND
      ? 'NOT_FOUND'
      : 'UNAVAILABLE'
    : null;

  useEffect(() => {
    if (!liveRankData) return;
    setLastFetchedTime(
      new Intl.DateTimeFormat(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(new Date())
    );
  }, [liveRankData]);

  return {
    liveRankDataIsLoading,
    liveRankData,
    lastFetchedTime,
    liveRankError,
    isLiveRankError: isError
  };
};
